import { SetStateAction, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import { LAYOUT_TYPE, PATTERN, SUCCESS_MESSAGE_TIMEOUT } from "../../constants";
import {
  createApplication,
  getApplication,
  isApplicationUnique,
  modifyApplication,
} from "../../api/applicationApi";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import CardTextInput from "../../components/CardTextInput/CardTextInput";
import SuccessMessage from "../../components/SuccessMessage/SuccessMessage";

export default function CreateEditApplication() {
  const [application, setApplication] = useState<any>();
  const [name, setName] = useState("");
  const [packageName, setPackageName] = useState("");
  const [version, setVersion] = useState(0);
  const [validated, setValidated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applicationLoadError, setApplicationLoadError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const params = useParams();
  const navigate = useNavigate();
  const isEditMode = params.applicationId ? true : false;

  useEffect(() => {
    if (params.applicationId) {
      getApplication(
        Number(params.applicationId),
        (responseData: SetStateAction<any>) => {
          setApplication(responseData);
          setName(responseData.name);
          setPackageName(responseData.packageName);
          setVersion(responseData.version);
        },
        (errorText: React.SetStateAction<string>) => {
          setApplicationLoadError(errorText);
        }
      );
    }
  }, [params.applicationId]);

  const handleChange = (event: any) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    if (name === "name") {
      setName(value);
    }
    if (name === "packageName") {
      setPackageName(value);
    }
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.stopPropagation();
    } else {
      setSubmitting(true);

      if (isEditMode) {
        if (application.name === name) {
          // no values were changed
          setSuccess("Application was modified.");
          setTimeout(() => {
            navigate(-1);
          }, SUCCESS_MESSAGE_TIMEOUT);
        } else {
          modifyApplication(
            Number(params.applicationId),
            name,
            version,
            () => {
              setSuccess("Application was modified.");
              setTimeout(() => {
                navigate(-1);
              }, SUCCESS_MESSAGE_TIMEOUT);
            },
            (errorText: React.SetStateAction<string>) => {
              setSubmitting(false);
              setError(errorText);
            }
          );
        }
      } else {
        isApplicationUnique(
          packageName,
          (responseData: SetStateAction<any>) => {
            if (responseData.unique) {
              createApplication(
                name,
                packageName,
                (responseData: SetStateAction<any>) => {
                  setSuccess("New application was created.");
                  setError("");
                  setTimeout(() => {
                    navigate("/application/" + responseData.id, {
                      replace: true,
                    });
                  }, SUCCESS_MESSAGE_TIMEOUT);
                },
                (errorText: React.SetStateAction<string>) => {
                  setSubmitting(false);
                  setError(errorText);
                }
              );
            } else {
              setSubmitting(false);
              setError("Application package name is not unique!");
            }
          },
          (errorText: React.SetStateAction<string>) => {
            setSubmitting(false);
            setError(errorText);
          }
        );
      }
    }

    setValidated(true);
  };

  if (applicationLoadError) {
    return <ErrorMessage text={applicationLoadError} className="m-5" />;
  }

  return (
    <>
      {error && (
        <ErrorMessage
          text={error}
          inContainer={true}
          containerClassName="form-container"
        />
      )}
      {success && (
        <SuccessMessage text={success} containerClassName="form-container" />
      )}
      <Layout
        type={LAYOUT_TYPE.FORM_PAGE}
        title={isEditMode ? "Edit Application" : "Submit Application"}
        buttons={[
          {
            title: "Cancel",
            variant: "outline-primary",
            action: () => {
              navigate(-1);
            },
            disabled: submitting,
          },
          {
            title: isEditMode ? "Save" : "Submit",
            variant: "primary",
            typeSubmit: true,
            withSpinner: true,
            disabled: submitting,
          },
        ]}
        onFormSubmit={handleSubmit}
        validated={validated}
      >
        <CardTextInput
          title="Basic Details"
          inputs={[
            {
              name: "name",
              label: "Name",
              placeholder: "Name",
              requiredText: "Name is missing!",
              value: name,
              onChange: handleChange,
            },
            {
              name: "packageName",
              label: "Package",
              placeholder: "Package",
              requiredText: "Package is missing!",
              pattern: PATTERN.PACKAGE,
              patternViolationText: "Package is invalid!",
              value: packageName,
              readOnly: isEditMode,
              onChange: handleChange,
            },
          ]}
          disabled={submitting}
        />
      </Layout>
    </>
  );
}
